<template>
  <div class="error-code-detail-wrapper">
    <div v-if="$apollo.queries.eventCode.loading" class="d-flex justify-center mt-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div v-else-if="eventCode">
      <div class="mb-2 d-flex align-center justify-space-between">
        <h3 class="mr-2">{{ $t('global.eventCodeDetail.title') }}</h3>
        <div class="d-flex">
          <v-btn
            v-if="!eventCode.deprecatedAt"
            @click="deprecateDialogOpen = true"
            color="warning"
            class="mr-2"
            style="height: 27px"
            outlined
          >
            {{ $t('global.adminEventCode.deprecate') }}
          </v-btn>
          <v-btn
            @click="updateEventCodeModalOpen = true"
            color="primary"
            style="text-decoration: none; height: 27px"
            depressed
          >
            {{ $t('global.eventCodeDetail.editAll') }}
          </v-btn>
          <v-btn
            @click="confirmDeleteModalOpen = true"
            class="ml-2"
            color="error"
            style="height: 27px"
            depressed
          >
            {{ $t('global.delete') }}
          </v-btn>
        </div>
      </div>
      <div class="values-wrapper mb-4">
        <div class="value">
          <v-btn small icon class="edit-btn" @click="editModalOpen = 'name'">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span class="value--headline">{{ this.$t('global.id') }}</span>
          <span class="value--text">{{ eventCode.name }}</span>
        </div>
        <div class="value">
          <v-btn small icon class="edit-btn" @click="editModalOpen = 'severity'">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span class="value--headline">{{ $t('global.adminEventCode.severity') }}</span>
          <span class="value--text">{{ eventCode.severity }}</span>
        </div>
        <div class="value">
          <v-btn small icon class="edit-btn" @click="editModalOpen = 'eventDetectionPointId'">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span class="value--headline">{{ $t('global.adminEventCode.detectionPoint') }}</span>
          <span class="value--text">{{
            eventCode.detectionPoint ? eventCode.detectionPoint.name : '---'
          }}</span>
        </div>
        <div class="value">
          <v-btn small icon class="edit-btn" @click="editModalOpen = 'disabled'">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span class="value--headline">{{ $t('global.state') }}</span>
          <span class="value--text">{{
            activationStatusMap[eventCode.disabled] || eventCode.disabled
          }}</span>
        </div>
        <div class="value">
          <v-btn small icon class="edit-btn" @click="editModalOpen = 'defaultNotificationClass'">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span class="value--headline">{{ $t('global.defaultNotificationClass') }}</span>
          <NotificationClass
            v-if="eventCode.defaultNotificationClass != null"
            class="mt-1"
            :notificationClass="eventCode.defaultNotificationClass.notificationClass"
          />
          <span v-else class="value--text">---</span>
        </div>
      </div>
      <h4>{{ $t('global.descriptions') }}</h4>
      <div class="values-wrapper mb-4">
        <div class="value" v-for="lang of allowedLanguages" :key="lang">
          <v-btn
            small
            icon
            class="edit-btn"
            @click="editModalOpen = `descriptionTranslations.${lang}`"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span
            ><v-icon size="18">{{ `$${lang}` }}</v-icon></span
          >
          <span class="value--text">{{ eventCode[`${lang}Description`] || '---' }}</span>
        </div>
      </div>
      <div class="long-values-wrapper mb-5">
        <div
          v-if="eventCode.deprecatedAt || eventCode.supersededEventCode"
          class="values-wrapper mb-2 deprecation-banners"
        >
          <div v-if="eventCode.deprecatedAt" class="value value--deprecation">
            <div class="value__row">
              <span class="value--headline">{{ $t('global.adminEventCode.deprecatedLabel') }}</span>
              <span class="value--text">{{ formatDate(eventCode.deprecatedAt) }}</span>
            </div>
            <div v-if="eventCode.replacementEventCode" class="mt-1">
              <strong>{{ $t('global.adminEventCode.replacement') }}:</strong>
              <router-link
                :to="{
                  name: 'AdminEventCodeDetail',
                  params: { id: eventCode.replacementEventCode.id },
                }"
              >
                {{ eventCode.replacementEventCode.name }}
              </router-link>
            </div>
          </div>
          <div v-if="eventCode.supersededEventCode" class="value value--derived">
            <span class="value--headline">{{ $t('global.adminEventCode.derivedFrom') }}</span>
            <span class="value--text">
              <router-link
                :to="{
                  name: 'AdminEventCodeDetail',
                  params: { id: eventCode.supersededEventCode.id },
                }"
              >
                {{ eventCode.supersededEventCode.name }}
              </router-link>
            </span>
          </div>
        </div>
        <div class="value">
          <v-btn small icon class="edit-btn" @click="editModalOpen = 'detectionMechanism'">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span class="value--headline">{{ $t('global.adminEventCode.detectionMechanism') }}</span>
          <span class="value--text">{{ eventCode.detectionMechanism || '---' }}</span>
        </div>
        <div class="value">
          <v-btn small icon class="edit-btn" @click="editModalOpen = 'terminationMechanism'">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span class="value--headline">{{
            $t('global.adminEventCode.terminationMechanism')
          }}</span>
          <span class="value--text">{{ eventCode.terminationMechanism || '---' }}</span>
        </div>
        <div class="value">
          <v-btn small icon class="edit-btn" @click="editModalOpen = 'actionOnEvent'">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <span class="value--headline">{{ $t('global.adminEventCode.actionOnEvent') }}</span>
          <span class="value--text">{{ eventCode.actionOnEvent || '---' }}</span>
        </div>
      </div>
      <div class="mb-4 d-flex align-center flex-wrap">
        <h3 class="mr-5">{{ $t('global.coreCauses') }}</h3>
        <v-btn height="27" depressed :to="{ name: 'AdminCoreCauses' }">{{
          $t('global.manage')
        }}</v-btn>
      </div>
      <div class="assignment-section">
        <div class="available-causes-wrapper">
          <h4 class="mb-5">{{ $t('global.adminEventCode.availableCauses') }}</h4>
          <v-text-field
            v-model="searchAvailable"
            :label="$t('users.search')"
            clearable
            class="mx-4"
          ></v-text-field>
          <div class="d-flex flex-column">
            <div
              v-for="coreCause of filteredAvailableCoreCauses"
              :key="coreCause.id"
              class="available-cause"
            >
              <div class="texts-wrapper">
                <router-link :to="{ name: 'AdminCoreCauseDetail', params: { id: coreCause.id } }">
                  <span class="description">{{ coreCause.description }}</span>
                  <span class="name">{{ coreCause.name }}</span>
                </router-link>
              </div>
              <v-btn icon @click="assignCoreCause(coreCause.id)"><v-icon>$plus</v-icon></v-btn>
            </div>
          </div>
        </div>
        <div>
          <h4 class="mb-5">{{ $t('global.adminEventCode.assignedCauses') }}</h4>
          <div class="d-flex flex-column" ref="sortableCoreCauses">
            <div v-for="coreCause of coreCauses" :key="coreCause.id" class="assigned-cause">
              <v-icon class="sort-handle">mdi-drag-horizontal-variant</v-icon>
              <div class="texts-wrapper">
                <router-link :to="{ name: 'AdminCoreCauseDetail', params: { id: coreCause.id } }">
                  <span class="description">{{ coreCause.description }}</span>
                  <span class="name">{{ coreCause.name }}</span>
                </router-link>
              </div>
              <v-btn icon @click="openUnassignCoreCauseConfirmationDialog(coreCause)" color="error"
                ><v-icon>mdi-close</v-icon></v-btn
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="pa-8">
      <v-alert prominent type="error">
        <v-row align="center">
          <v-col class="grow">
            {{ $t('global.eventCodeDetail.eventCodeNotFound') }}
          </v-col>
          <v-col class="shrink">
            <v-btn @click="$router.back()">{{ $t('global.back') }}</v-btn>
          </v-col>
        </v-row>
      </v-alert>
    </div>

    <template v-if="eventCode">
      <EditValueModal
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === 'name'"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.id')"
        :validations="requiredValidation"
        :originalValue="eventCode.name"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-text-field
          :value="value"
          @input="onInput"
          :error-messages="errors"
          :label="$t('global.id')"
          @blur="validations.value.$touch()"
          filled
          style="width: 330px"
        ></v-text-field>
      </EditValueModal>
      <EditValueModal
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === 'severity'"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.adminEventCode.severity')"
        :validations="requiredValidation"
        :originalValue="eventCode.severity"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-select
          :items="errorSeverities"
          :error-messages="errors"
          :label="$t('global.adminEventCode.severity')"
          :value="value"
          @input="onInput"
          @blur="validations.value.$touch()"
          filled
          style="width: 330px"
        ></v-select>
      </EditValueModal>
      <EditValueModal
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === 'eventDetectionPointId'"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.adminEventCode.detectionPoint')"
        :validations="requiredValidation"
        :originalValue="eventCode.detectionPoint.id"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-select
          :items="detectionPoints"
          :error-messages="errors"
          :label="$t('global.adminEventCode.detectionPoint')"
          item-text="name"
          item-value="id"
          :value="value"
          @input="onInput"
          @blur="validations.value.$touch()"
          filled
          style="width: 330px"
        ></v-select>
      </EditValueModal>
      <EditValueModal
        v-slot="{ value, onInput }"
        :isOpen="editModalOpen === 'disabled'"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.state')"
        :originalValue="eventCode.disabled"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-select
          class="mt-3"
          :items="activationStatusItems"
          :value="value"
          @input="onInput"
          :label="$t('global.state')"
          filled
          style="width: 330px"
        ></v-select>
      </EditValueModal>
      <EditValueModal
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === 'defaultNotificationClass'"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.defaultNotificationClass')"
        :validations="requiredValidation"
        :originalValue="eventCode.defaultNotificationClass.notificationClass"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-select
          :items="notificationClasses"
          :error-messages="errors"
          :label="$t('global.defaultNotificationClass')"
          :value="value"
          @input="onInput"
          @blur="validations.value.$touch()"
          filled
          style="width: 330px"
        >
          <template v-slot:selection="{ item }">
            <NotificationClass class="mt-2" :notificationClass="item" />
          </template>
          <template v-slot:item="{ active, item, attrs, on }">
            <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
              <NotificationClass :notificationClass="item" />
            </v-list-item>
          </template>
        </v-select>
      </EditValueModal>
      <EditValueModal
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === 'exclusiveCompanyId'"
        :canDelete="true"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.exclusiveCompany')"
        :validations="requiredValidation"
        :originalValue="eventCode.detectionPoint.id"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-select
          :items="companies"
          :error-messages="errors"
          :label="$t('global.exclusiveCompany')"
          item-text="name"
          item-value="id"
          :value="eventCode.exclusiveCompanyId"
          @input="onInput"
          @blur="validations.value.$touch()"
          filled
          style="width: 330px"
        ></v-select>
      </EditValueModal>
      <EditValueModal
        v-for="lang of allowedLanguages"
        :key="lang"
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === `descriptionTranslations.${lang}`"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.adminEventCode.description')"
        :validations="maxLengthValidation"
        :originalValue="eventCode[`${lang}Description`]"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-textarea
          :value="value"
          :error-messages="errors"
          @input="onInput"
          @blur="validations.value.$touch()"
          filled
          counter="255"
          rows="4"
          style="width: 330px"
        >
          <template v-slot:label>
            {{ $t('global.adminEventCode.description') }}
            <v-icon class="ml-1" style="vertical-align: middle">{{ `$${lang}` }}</v-icon>
          </template>
        </v-textarea>
      </EditValueModal>
      <EditValueModal
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === 'detectionMechanism'"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.adminEventCode.detectionMechanism')"
        :validations="maxLengthValidation"
        :originalValue="eventCode.detectionMechanism"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-textarea
          :label="$t('global.adminEventCode.detectionMechanism')"
          :error-messages="errors"
          :value="value"
          @input="onInput"
          @blur="validations.value.$touch()"
          filled
          style="width: 330px"
          counter="255"
        ></v-textarea>
      </EditValueModal>
      <EditValueModal
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === 'terminationMechanism'"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.adminEventCode.terminationMechanism')"
        :validations="maxLengthValidation"
        :originalValue="eventCode.terminationMechanism"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-textarea
          :label="$t('global.adminEventCode.terminationMechanism')"
          :error-messages="errors"
          :value="value"
          @input="onInput"
          @blur="validations.value.$touch()"
          filled
          style="width: 330px"
          counter="255"
        ></v-textarea>
      </EditValueModal>
      <EditValueModal
        v-slot="{ validations, value, onInput, errors }"
        :isOpen="editModalOpen === 'actionOnEvent'"
        @modalToggle="editModalOpen = null"
        :heading="$t('global.adminEventCode.actionOnEvent')"
        :validations="maxLengthValidation"
        :originalValue="eventCode.actionOnEvent"
        :submit="updateEventCode"
        :closeBtnText="$t('global.cancel')"
        :submitBtnText="$t('global.save')"
      >
        <v-textarea
          :label="$t('global.adminEventCode.actionOnEvent')"
          :error-messages="errors"
          :value="value"
          @input="onInput"
          @blur="validations.value.$touch()"
          filled
          style="width: 330px"
          counter="255"
        ></v-textarea>
      </EditValueModal>

      <ConfirmationDialog
        v-model="confirmDeleteModalOpen"
        :heading="$t('global.eventCodeDetail.deleteConfirmationModal.heading')"
        :text="
          $t('global.eventCodeDetail.deleteConfirmationModal.text', { eventCode: eventCode.name })
        "
        :confirmText="$t('global.delete')"
        confirmBtnColor="error"
        :action="deleteEventCode"
      />
    </template>

    <ConfirmationDialog
      v-if="coreCauseToUnassign"
      v-model="unassignCoreCauseConfirmationModalOpen"
      :heading="$t('global.eventCodeDetail.unassignCoreCauseConfirmationModal.heading')"
      :text="
        $t('global.eventCodeDetail.unassignCoreCauseConfirmationModal.text', {
          coreCause: coreCauseToUnassign.name,
        })
      "
      :confirmText="$t('global.unassign')"
      confirmBtnColor="error"
      :action="unassignCoreCause"
    />

    <UpdateEventCodeModal
      v-model="updateEventCodeModalOpen"
      :eventCode="eventCode"
      :refetchQueries="
        inModal ? ['eventCodeAdmin', 'serviceHintsAdminOverview'] : ['eventCodeAdmin']
      "
    />
    <DeprecateEventDialog
      v-model="deprecateDialogOpen"
      :eventCode="eventCode"
      @created="onDeprecatedCreated"
    />
  </div>
</template>
<script>
import { required, maxLength } from 'vuelidate/lib/validators'
import { eventCodeAdmin } from '@/graphql/query/eventCodeAdmin'
import { eventCoreCausesAdmin } from '@/graphql/query/eventCoreCausesAdmin'
import { eventCodeAssignCoreCause } from '@/graphql/mutations/eventCodeAssignCoreCause'
import { eventCodeUnassignCoreCause } from '@/graphql/mutations/eventCodeUnassignCoreCause'
import { eventCodeReorderCoreCauses } from '@/graphql/mutations/eventCodeReorderCoreCauses'
import { eventCodeDelete } from '@/graphql/mutations/eventCodeDelete'
import UpdateEventCodeModal from '@/components/Admin/ServiceHints/UpdateEventCodeModal'
import EditValueModal from '@/components/Admin/ServiceHints/EditValueModal'
import { eventCodeUpdate } from '@/graphql/mutations/eventCodeUpdate'
import pick from 'lodash/pick'
import { errorsConfig } from '@/config/errorsConfig'
import { eventDetectionPointsAdmin } from '@/graphql/query/eventDetectionPointsAdmin'
import Sortable from 'sortablejs'
import { allowedLanguages } from '@/translations'
import set from 'lodash/set'
import NotificationClass from '@/components/NotificationDispatcher/NotificationClass'
import ConfirmationDialog from '@/components/ConfirmationDialog'
import { notificationClassesEnumValues } from '@/graphql/query/notificationClassesEnumValues'
import DeprecateEventDialog from '@/components/Admin/ServiceHints/DeprecateEventDialog'
import { eventDeactivationState } from '@/config/eventDeactivation'

export default {
  name: 'AdminEventCodeDetailPage',
  components: {
    UpdateEventCodeModal,
    EditValueModal,
    NotificationClass,
    ConfirmationDialog,
    DeprecateEventDialog,
  },
  props: {
    id: String,
    inModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      coreCauses: null,
      confirmDeleteModalOpen: false,
      coreCauseToUnassign: null,
      unassignCoreCauseConfirmationModalOpen: false,
      updateEventCodeModalOpen: false,
      deprecateDialogOpen: false,
      editModalOpen: null,
      requiredValidation: {
        value: { required },
      },
      maxLengthValidation: {
        value: { maxLength: maxLength(255) },
      },
      searchAvailable: null,
    }
  },
  apollo: {
    eventCode: {
      query: eventCodeAdmin,
      variables() {
        return {
          id: this.id,
          lang: this.$i18n.locale,
        }
      },
      update(data) {
        this.coreCauses = JSON.parse(JSON.stringify(data.eventCodeAdmin.coreCauses))
        return data.eventCodeAdmin
      },
    },
    eventCoreCausesAdmin: {
      query: eventCoreCausesAdmin,
      variables() {
        return {
          lang: this.$i18n.locale,
        }
      },
    },
    detectionPoints: {
      query: eventDetectionPointsAdmin,
      update(data) {
        return data.eventDetectionPointsAdmin
      },
    },
    notificationClasses: {
      query: notificationClassesEnumValues,
      update(data) {
        return data.__type.enumValues.map((value) => value.name)
      },
    },
  },
  methods: {
    async updateEventCode(value) {
      const key = this.editModalOpen
      try {
        const nullInsteadOfEmptyString = (string) => (string?.trim() === '' ? null : string)
        const newValue = [
          'detectionMechanism',
          'terminationMechanism',
          'actionOnEvent',
          ...allowedLanguages.map((lang) => `descriptionTranslations.${lang}`),
        ].includes(key)
          ? nullInsteadOfEmptyString(value)
          : value

        const data = {
          eventCodeId: this.eventCode.id,
          eventDetectionPointId: this.eventCode.detectionPoint?.id,
          defaultNotificationClass:
            this.eventCode.defaultNotificationClass?.notificationClass || 'NO_NOTIFICATION',
          ...pick(
            this.eventCode,
            'name',
            'severity',
            'disabled',
            'detectionMechanism',
            'terminationMechanism',
            'actionOnEvent',
          ),
          descriptionTranslations: Object.fromEntries(
            allowedLanguages.map((lang) => [lang, this.eventCode[`${lang}Description`]]),
          ),
        }

        // update key
        set(data, key, newValue)

        await this.$apollo.mutate({
          mutation: eventCodeUpdate,
          variables: {
            data,
          },
          refetchQueries:
            key === 'eventDetectionPointId' && this.inModal
              ? ['eventCodeAdmin', 'serviceHintsAdminOverview']
              : ['eventCodeAdmin'],
        })

        this.$toast.success(this.$t('global.eventCodeDetail.updateEventCodeAction.success'))
        this.editModalOpen = null
      } catch (error) {
        console.error(error)
        const parsedError = error.message?.replace('GraphQL error:', '').trim()
        this.$toast.error(
          parsedError === 'EVENT_CODE_UPDATE_NAME_TAKEN'
            ? this.$t('global.nameTakenError')
            : this.$t('global.eventCodeDetail.updateEventCodeAction.failure'),
        )
      }
    },
    async assignCoreCause(newCoreCauseId) {
      try {
        await this.$apollo.mutate({
          mutation: eventCodeAssignCoreCause,
          variables: {
            eventCodeId: this.id,
            coreCauseId: newCoreCauseId,
          },
          refetchQueries: ['eventCodeAdmin'],
        })

        this.$toast.success(this.$t('global.eventCodeDetail.assignCoreCauseAction.success'))
      } catch (error) {
        console.error(error)
        this.$toast.error(this.$t('global.eventCodeDetail.assignCoreCauseAction.failure'))
      }
    },
    async deleteEventCode() {
      try {
        await this.$apollo.mutate({
          mutation: eventCodeDelete,
          variables: {
            eventCodeId: this.id,
          },
          refetchQueries: this.inModal ? ['serviceHintsAdminOverview'] : [],
        })

        this.$toast.success(this.$t('global.eventCodeDetail.deleteEventCodeAction.success'))
        if (this.inModal) {
          this.$emit('onDelete')
        } else {
          this.$router.push({ name: 'AdminEventCodes' })
        }
      } catch (error) {
        console.error(error)
        this.$toast.error(this.$t('global.eventCodeDetail.deleteEventCodeAction.failure'))
      }
    },
    async unassignCoreCause() {
      try {
        await this.$apollo.mutate({
          mutation: eventCodeUnassignCoreCause,
          variables: {
            eventCodeId: this.id,
            coreCauseId: this.coreCauseToUnassign.id,
          },
          refetchQueries: ['eventCodeAdmin'],
        })

        this.$toast.success(this.$t('global.eventCodeDetail.unassignCoreCauseAction.success'))
        this.unassignCoreCauseConfirmationModalOpen = false
      } catch (error) {
        console.error(error)
        this.$toast.error(this.$t('global.eventCodeDetail.unassignCoreCauseAction.failure'))
      }
    },
    async reorderCoreCauses(orderedCoreCauseIds) {
      try {
        await this.$apollo.mutate({
          mutation: eventCodeReorderCoreCauses,
          variables: {
            eventCodeId: this.id,
            orderedCoreCauseIds,
          },
          refetchQueries: this.inModal ? ['serviceHintsAdminOverview'] : [],
        })

        this.$toast.success(this.$t('global.eventCodeDetail.reorderCoreCausesAction.success'))
      } catch (error) {
        console.error(error)
        this.$toast.error(this.$t('global.eventCodeDetail.reorderCoreCausesAction.failure'))
      }
    },
    async saveOrder(event) {
      const movedItem = this.coreCauses.splice(event.oldIndex, 1)[0]
      this.coreCauses.splice(event.newIndex, 0, movedItem)

      await this.reorderCoreCauses(this.coreCauses.map((cc) => cc.id))
    },
    openUnassignCoreCauseConfirmationDialog(coreCause) {
      this.coreCauseToUnassign = coreCause
      this.unassignCoreCauseConfirmationModalOpen = true
    },
    onDeprecatedCreated(newEventId) {
      this.$router.push({ name: 'AdminEventCodeDetail', params: { id: newEventId } })
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      // Try native ISO parsing first
      const d = new Date(dateStr)
      if (!isNaN(d.getTime())) return d.toLocaleString()
      // Handle numeric epoch (seconds or milliseconds)
      const isDigits = typeof dateStr === 'number' || /^\d+$/.test(String(dateStr))
      if (isDigits) {
        const s = String(dateStr)
        let epochMs = Number(s)
        if (s.length === 10) epochMs *= 1000 // seconds -> ms
        const dNum = new Date(epochMs)
        if (!isNaN(dNum.getTime())) return dNum.toLocaleString()
      }
      // Normalize common Postgres format: 'YYYY-MM-DD HH:mm:ss[.SSS][+TZ]' -> replace space with 'T'
      const normalized = dateStr.replace(' ', 'T')
      const d2 = new Date(normalized)
      if (!isNaN(d2.getTime())) return d2.toLocaleString()
      // Fallback to original string
      return dateStr
    },
  },
  computed: {
    availableCoreCauses() {
      if (!this.eventCoreCausesAdmin || !this.coreCauses) {
        return []
      }
      return this.eventCoreCausesAdmin.filter(
        (cc) => !this.coreCauses.find((assignedCoreCause) => assignedCoreCause.id === cc.id),
      )
    },
    filteredAvailableCoreCauses() {
      if (!this.availableCoreCauses.length) {
        return []
      }
      if (!this.searchAvailable || this.searchAvailable.trim() === '') {
        return this.availableCoreCauses
      }
      const lowerCaseSearch = this.searchAvailable.toLowerCase()
      return this.availableCoreCauses.filter(
        (cc) =>
          cc.name.toLowerCase().indexOf(lowerCaseSearch) !== -1 ||
          (cc.description && cc.description.toLowerCase().indexOf(lowerCaseSearch) !== -1),
      )
    },
    errorSeverities() {
      return Object.keys(errorsConfig)
    },
    allowedLanguages() {
      return allowedLanguages
    },
    activationStatusMap() {
      return {
        DEACTIVATED: this.$t('global.adminEventCode.disabled'),
        ACTIVATED: this.$t('global.adminEventCode.active'),
        ELEDIO_ONLY: this.$t('global.adminEventCode.eledioOnly'),
      }
    },
    activationStatusItems() {
      return Object.keys(eventDeactivationState).map((key) => ({
        text: this.activationStatusMap[key] || key,
        value: key,
      }))
    },
  },
  watch: {
    coreCauses(newValue, oldValue) {
      if (!oldValue && newValue) {
        this.$nextTick(() => {
          Sortable.create(this.$refs.sortableCoreCauses, {
            animation: 150,
            handle: '.sort-handle',
            onUpdate: this.saveOrder,
          })
        })
      }
    },
  },
}
</script>
<style lang="less" scoped>
@import '~@/assets/less/variables.less';

.values-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  grid-gap: 5px;
}

.value {
  padding: 16px;
  background-color: @color-tile-background;
  position: relative;

  &--headline {
    display: block;
    opacity: 0.5;
    font-size: 12px;
    margin-right: 20px;
  }

  &--text {
    display: block;
    font-size: 14px;
    font-weight: bold;
    word-break: break-word;
  }

  .edit-btn {
    position: absolute;
    top: 5px;
    right: 5px;
  }
}

.long-values-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(600px, 1fr));
  grid-gap: 5px;
}

.deprecation-banners {
  .value {
    display: block;
    overflow: hidden;
    // ensure long strings never overflow
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .value--deprecation {
    background-color: fade(#ff9800, 12%);
    border-left: 4px solid #ff9800;
  }

  .value--derived {
    background-color: fade(#2196f3, 10%);
    border-left: 4px solid fade(#2196f3, 60%);
  }

  a {
    word-break: break-all;
  }
}

.assignment-section {
  display: flex;

  .available-causes-wrapper {
    margin-right: 30px;
  }

  .available-cause {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 5px 10px 15px;
    border-radius: 3px;
    background-color: tint(@color-brand-highlight, 50%);
    margin-bottom: 5px;
    word-break: break-word;
  }

  .assigned-cause {
    display: flex;
    align-items: center;
    padding: 10px 5px 10px 15px;
    background-color: @color-brand-highlight;
    margin-bottom: 5px;
    word-break: break-word;

    & > i {
      cursor: move;
      margin-right: 15px;
    }
  }

  .texts-wrapper {
    flex-grow: 1;
    margin-right: 5px;

    .description {
      display: block;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 3px;
    }

    .name {
      display: block;
      font-size: 12px;
      opacity: 0.75;
    }
  }

  @media (max-width: 600px) {
    flex-direction: column;

    .available-causes-wrapper {
      order: 2;
      margin-right: 0px;
      margin-top: 20px;
    }
  }
}

// Deprecation/Derived tiles layout helpers
.value__row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
